/* hs-eslint ignored failing-rules */
/* eslint-disable react-hooks/exhaustive-deps */

'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { Link } from 'foundations-components/transitional/components/Link';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { TYPEAHEAD_SEARCH_MIN_CHARS } from 'marketplace-ui-common/constants/search';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useScreenSize } from 'marketplace-ui-common/hooks/useScreenSize';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import InputHighlighter from 'marketplace-ui-common/search/components/InputHighlighter';
import SearchBar from 'marketplace-ui-common/search/components/SearchBar';
import { buildGetCachedSearchValues, buildSetCachedSearchValue } from 'marketplace-ui-common/search/utils';
import CoreStorefrontHeader from 'marketplace-ui-common/storefront/components/StorefrontHeader';
import { isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { debounce } from 'marketplace-ui-common/utils/debounce';
import * as Industries from 'marketplace-ui-solutions-core/constants/routes/IndustryRoute';
import { useProfileSearchBarQuery } from 'marketplace-ui-solutions-core/search/hooks/useProfileSearchBarQuery';
import Raven from 'raven-js';
import { Fragment, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { storefrontFiltersInitialState } from '../constants/StorefrontFilters';
import * as FilterKey from '../constants/search/FilterKey';
import { RELEVANCE_DESC } from '../constants/search/SortField';
import StorefrontUsageTrackerContext from '../context/StorefrontUsageTrackerContext';
import { getCanonicalAppContext, getIsPublicApp } from '../selectors/app';
import { getServices, getServicesRequestState } from '../slices/serviceCatalogSlice';
import { getMultiSelectStorefrontFilters, updateStorefrontFilters } from '../slices/storefrontFiltersSlice';
import { getSortOptionValue } from '../utils/filters';
import { push } from '../utils/history';
import { clearRecentActivity } from '../utils/homepage';
import { translateFilterValue } from '../utils/i18n';
import { getFullLocationUrl } from '../utils/urls';
import MobileStorefrontHeader from './MobileStorefrontHeader';
import { MarketplaceBreadcrumb } from './breadcrumbs/MarketplaceBreadcrumb';
import HeroSection from './homepage/HeroSection';
import FindPartnerWizard from './homepage/questionnaire/FindPartnerWizard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CACHED_SEARCH_VALUES_KEY = 'ecosystem-marketplace-solutions:searches';
const getCachedSearchValues = buildGetCachedSearchValues(CACHED_SEARCH_VALUES_KEY);
const setCachedSearchValue = buildSetCachedSearchValue(CACHED_SEARCH_VALUES_KEY);
const getProfileOptionsGroup = (items, input) => {
  const filteredOptions = items.map(result => result.item ? {
    dropdownText: result.dropdownText,
    slug: result.item.slug,
    text: result.text,
    title: result.text,
    value: result.item.id
  } : {
    dropdownText: /*#__PURE__*/_jsx(Link, {
      children: result.text
    }),
    text: result.text,
    value: input,
    viewAll: true
  });
  return {
    text: I18n.text(`ecosystem-marketplace-solutions-ui.storefront.header.search.typeaheadResultsHeader`),
    options: filteredOptions
  };
};
const getIndustryOptionsGroup = input => {
  const filteredOptions = Object.keys(Industries).filter(industry => translateFilterValue(FilterKey.INDUSTRY, industry).toLowerCase().includes(input)).map(industry => {
    const translatedText = translateFilterValue(FilterKey.INDUSTRY, industry);
    return {
      dropdownText: /*#__PURE__*/_jsx(InputHighlighter, {
        text: translatedText,
        value: input
      }),
      filterKey: FilterKey.INDUSTRY,
      text: translatedText,
      value: industry
    };
  });
  return {
    text: I18n.text(`ecosystem-marketplace-solutions-ui.storefront.filters.industry.label`),
    options: filteredOptions
  };
};
const StorefrontHeader = () => {
  const dispatch = useDispatch();
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const canonicalAppContext = useSelector(getCanonicalAppContext);
  const filters = useSelector(getMultiSelectStorefrontFilters);
  const catalogServices = useSelector(getServices);
  const servicesRequestState = useSelector(getServicesRequestState);
  const servicesAvailable = isSucceeded(servicesRequestState);
  const {
    browseAllPartnersMarginAdjustment,
    placeholder
  } = useResponsiveProps({
    placeholder: {
      xs: undefined,
      sm: I18n.text(`ecosystem-marketplace-solutions-ui.storefront.header.search.placeholder`)
    },
    browseAllPartnersMarginAdjustment: {
      xl: '-800',
      lg: '0px'
    }
  });
  const [showFindPartnerWizard, setShowFindPartnerWizard] = useState(false);
  const [currentOptions, setCurrentOptions] = useState([]);
  const currentSearchInputRef = useRef('');
  const cachedSearchValues = getCachedSearchValues();
  const recentSearchOptions = useMemo(() => {
    if (!cachedSearchValues.length) {
      return [];
    }
    return [{
      text: I18n.text(`ecosystem-marketplace-solutions-ui.storefront.header.search.recent`),
      options: cachedSearchValues.map(value => ({
        text: value,
        value,
        cachedSearchValue: true
      }))
    }];
  }, [cachedSearchValues]);
  const isOnHomepage = useMemo(() => {
    const homepageRegExp = new RegExp(/marketplace\/solutions(\/)?$/);
    return homepageRegExp.test(window.location.pathname);
  }, [window.location.pathname]);
  const getServiceOptionsGroup = useCallback(input => {
    const filteredOptions = catalogServices.filter(service => service.name.toLowerCase().includes(input)).map(service => {
      return {
        dropdownText: /*#__PURE__*/_jsx(InputHighlighter, {
          text: service.name,
          value: input
        }),
        filterKey: FilterKey.SERVICE,
        text: service.name,
        value: service.id
      };
    });
    return {
      text: I18n.text(`ecosystem-marketplace-solutions-ui.storefront.filters.service.label`),
      options: filteredOptions
    };
  }, [catalogServices]);
  const onSearchSubmit = useCallback(value => {
    const valuesFromCache = getCachedSearchValues();
    setCachedSearchValue(value, valuesFromCache);
    dispatch(updateStorefrontFilters({
      updates: Object.assign({}, storefrontFiltersInitialState, {
        [FilterKey.SEARCH]: value,
        [FilterKey.SORTS]: RELEVANCE_DESC
      })
    }));
    trackInteraction('submit search', {
      search: value,
      searchLength: value.length
    });
  }, [dispatch, trackInteraction]);
  const onClearSearch = useCallback(() => {
    if (filters[FilterKey.SEARCH]) {
      dispatch(updateStorefrontFilters({
        updates: {
          [FilterKey.SEARCH]: storefrontFiltersInitialState[FilterKey.SEARCH]
        }
      }));
      dispatch(updateStorefrontFilters({
        updates: {
          [FilterKey.SORTS]: storefrontFiltersInitialState[FilterKey.SORTS]
        }
      }));
    }
  }, [dispatch, filters]);
  const findSelectedOptionIndex = useCallback(selectedOption => {
    if (!currentOptions.length || !selectedOption) return 0;
    let position = 0;
    for (const group of currentOptions) {
      var _group$options;
      if (!((_group$options = group.options) !== null && _group$options !== void 0 && _group$options.length)) continue;

      // Find the selected option in this group
      const index = group.options.findIndex(opt => opt.value === selectedOption.value);
      if (index !== -1) return position + index;
      position += group.options.length;
    }
    return 0;
  }, [currentOptions]);
  const trackSelectedOptionInteraction = useCallback((itemType, value, option = null) => {
    // Skip itemIndex for 'see all results' and 'recent search'
    const itemIndex = itemType === 'see all results' || itemType === 'recent search' ? undefined : findSelectedOptionIndex(option);
    trackInteraction('select search autofill item', {
      itemType,
      value,
      search: currentSearchInputRef.current || filters[FilterKey.SEARCH],
      itemIndex
    });
  }, [trackInteraction, filters, findSelectedOptionIndex]);
  const onSelectedOptionChange = useCallback(({
    target: {
      value: option
    }
  }) => {
    const {
      value,
      filterKey,
      cachedSearchValue,
      slug,
      viewAll
    } = option;
    if (viewAll) {
      onSearchSubmit(value);
      trackSelectedOptionInteraction('see all results', value, option);
      return;
    }
    if (filterKey) {
      dispatch(updateStorefrontFilters({
        updates: {
          [filterKey]: [value]
        }
      }));
      trackSelectedOptionInteraction(filterKey, value, option);
      return;
    }
    if (cachedSearchValue) {
      onSearchSubmit(value);
      trackSelectedOptionInteraction('recent search', value, option);
      return;
    }
    push(`/${slug}`);
    trackSelectedOptionInteraction('profile', slug, option);
  }, [dispatch, onSearchSubmit, trackSelectedOptionInteraction]);
  const isPublicApp = useSelector(getIsPublicApp);
  const {
    fetcher
  } = useProfileSearchBarQuery({
    appContext: isPublicApp ? 'public' : 'app',
    query: {
      sorts: getSortOptionValue(RELEVANCE_DESC),
      callingUserLanguage: getLang()
    }
  });
  const buildOptionsFromRecords = (recordData, input) => {
    const profileOptions = getProfileOptionsGroup(recordData, input);
    const serviceOptions = getServiceOptionsGroup(input);
    const industryOptions = getIndustryOptionsGroup(input);
    return [serviceOptions, profileOptions, industryOptions];
  };
  const loadOptions = useCallback(debounce((input, callback) => {
    if (input.trim().length < TYPEAHEAD_SEARCH_MIN_CHARS) {
      const options = recentSearchOptions;
      setCurrentOptions(options);
      callback(null, {
        options
      });
      return;
    }
    fetcher(input).then(resp => {
      const options = buildOptionsFromRecords(resp, input);
      setCurrentOptions(options);
      callback(null, {
        options
      });
    }).catch(() => {
      Raven.captureMessage('Profile typeahead search failed', {
        level: 'error',
        extra: {
          search: input
        }
      });
      const options = buildOptionsFromRecords([], input);
      setCurrentOptions(options);
      callback(null, {
        options
      });
    });
  }), [filters, recentSearchOptions]);
  const screenSize = useScreenSize();
  const isMobileView = screenSize === ScreenSizes.XS || screenSize === ScreenSizes.SM;
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(CoreStorefrontHeader, {
      backgroundImage: "none",
      collapsedTitle: /*#__PURE__*/_jsx(Link, {
        href: getFullLocationUrl({
          pathname: '/'
        }, canonicalAppContext),
        use: "unstyled",
        children: /*#__PURE__*/_jsx(Text, {
          as: "span",
          color: "text-core-default",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `ecosystem-marketplace-solutions-ui.storefront.header.collapsedTitle`
          })
        })
      }),
      color: "white",
      use: "collapsed",
      renderedBreadcrumbs: /*#__PURE__*/_jsx(MarketplaceBreadcrumb, {}),
      children: [/*#__PURE__*/_jsx(MediaQueryView, {
        min: ScreenSizes.SM,
        children: /*#__PURE__*/_jsxs(TFlex, {
          justify: "between",
          children: [/*#__PURE__*/_jsx(MediaQueryView, {
            min: isOnHomepage ? ScreenSizes.SM : ScreenSizes.LG,
            children: /*#__PURE__*/_jsx(Button, {
              "data-test-id": "browse-all-partners-button",
              marginInlineStart: browseAllPartnersMarginAdjustment,
              onClick: () => {
                trackInteraction('click browse all partners');
                push('/all');
              },
              use: "unstyled",
              truncate: true,
              paddingBlockStart: "300",
              marginInlineEnd: "400",
              children: /*#__PURE__*/_jsx(Text, {
                as: "h5",
                color: "text-core-default",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `ecosystem-marketplace-solutions-ui.storefront.header.browseAll`
                })
              })
            })
          }), /*#__PURE__*/_jsxs(TFlex, {
            justify: "end",
            children: [!isOnHomepage && servicesAvailable && /*#__PURE__*/_jsx(MediaQueryView, {
              min: ScreenSizes.MD,
              children: /*#__PURE__*/_jsx(Button, {
                "data-test-id": "findPartnerWizard-button",
                use: "secondary-ghost",
                onClick: () => {
                  trackInteraction('click find a partner');
                  clearRecentActivity();
                  setShowFindPartnerWizard(true);
                },
                marginInlineEnd: "200",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `ecosystem-marketplace-solutions-ui.storefront.header.findPartner`
                })
              })
            }), /*#__PURE__*/_jsx("div", {
              style: {
                flexGrow: 'inherit',
                maxWidth: 400
              },
              children: /*#__PURE__*/_jsx(SearchBar, {
                loadOptions: loadOptions,
                inputSize: 'default',
                inputValue: filters[FilterKey.SEARCH],
                onClear: onClearSearch,
                onSearchSubmit: onSearchSubmit,
                onSelectedOptionChange: onSelectedOptionChange,
                onInputValueChange: event => {
                  currentSearchInputRef.current = event.target.value;
                },
                placeholder: placeholder
              })
            })]
          })]
        })
      }), /*#__PURE__*/_jsx(MediaQueryView, {
        max: ScreenSizes.SM,
        children: /*#__PURE__*/_jsx(MobileStorefrontHeader, {
          onClearSearch: onClearSearch,
          onSearchSubmit: onSearchSubmit,
          loadOptions: loadOptions,
          onSelectedOptionChange: onSelectedOptionChange,
          placeholder: placeholder,
          searchTerm: filters[FilterKey.SEARCH],
          color: "white"
        })
      })]
    }), /*#__PURE__*/_jsx(View, {
      borderBlockColor: "border-core-default",
      borderBlockStyle: "solid",
      borderBlockWidth: "100",
      children: isOnHomepage && /*#__PURE__*/_jsx(HeroSection, {
        onShowFindPartnerWizard: () => {
          if (servicesAvailable) {
            trackInteraction('click find a partner');
            clearRecentActivity();
            setShowFindPartnerWizard(true);
          }
        },
        isMobileView: isMobileView,
        headerColor: "white",
        servicesAvailable: servicesAvailable
      })
    }), showFindPartnerWizard && /*#__PURE__*/_jsx(FindPartnerWizard, {
      onClose: () => setShowFindPartnerWizard(false),
      isMobileView: isMobileView
    })]
  });
};
export default StorefrontHeader;